export function parseTime(str) {
  if (!str) return null;
  const parts = str.split(":").map((p) => parseFloat(p));
  if (parts.some((p) => isNaN(p) || p < 0)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0] * 60;
  return null;
}

export function fmtTime(seconds) {
  if (seconds == null || !isFinite(seconds)) return "--";
  seconds = Math.round(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function fmtPace(secPerUnit) {
  if (secPerUnit == null || !isFinite(secPerUnit)) return "--";
  const m = Math.floor(secPerUnit / 60);
  const s = Math.round(secPerUnit % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const DISTANCES = {
  "1K": 1, "1 Mile": 1.60934, "5K": 5, "10K": 10, "15K": 15,
  "Half Marathon": 21.0975, "Marathon": 42.195, "50K": 50,
};

export function riegel(t1, d1, d2) {
  return t1 * Math.pow(d2 / d1, 1.06);
}

export function trainingZonesFrom5k(fiveKSeconds) {
  const p = fiveKSeconds / 5; // sec per km at 5K pace
  return [
    { zone: "Easy / Recovery", mult: [1.25, 1.42], desc: "Conversational. Most of your weekly volume lives here." },
    { zone: "Marathon effort", mult: [1.13, 1.2], desc: "Steady. Sustainable for hours when trained." },
    { zone: "Threshold", mult: [1.04, 1.08], desc: "Comfortably hard. Roughly one-hour race effort." },
    { zone: "Interval / VO2", mult: [0.97, 1.0], desc: "Hard 3 to 5 minute reps. 2 to 3 words at a time." },
    { zone: "Repetition / Speed", mult: [0.9, 0.94], desc: "Short, fast, relaxed. Full recoveries." },
  ].map((z) => ({
    ...z,
    perKm: [p * z.mult[0], p * z.mult[1]],
    perMi: [p * 1.60934 * z.mult[0], p * 1.60934 * z.mult[1]],
  }));
}

export function mileageProgression(current, target, weeks) {
  const plan = [];
  let mileage = current;
  for (let w = 1; w <= weeks; w++) {
    const isDown = w % 4 === 0 && w !== weeks;
    if (isDown) {
      plan.push({ week: w, mileage: Math.round(mileage * 0.8 * 10) / 10, down: true });
    } else {
      plan.push({ week: w, mileage: Math.round(mileage * 10) / 10, down: false });
      mileage = Math.min(target, mileage * 1.1);
    }
  }
  return plan;
}

export const QUIZ = [
  {
    q: "What gets you out the door?",
    key: "vibe",
    options: [
      { label: "People and post-run plans", value: "post-run social" },
      { label: "A structured workout", value: "training group" },
      { label: "Chasing times", value: "competitive" },
      { label: "Just consistent easy miles", value: "community" },
    ],
  },
  {
    q: "Pick your surface",
    key: "surface",
    options: [
      { label: "City streets", value: "community" },
      { label: "The track", value: "track" },
      { label: "Dirt and hills", value: "trail" },
      { label: "Wherever the group goes", value: "community" },
    ],
  },
  {
    q: "When do you want to run?",
    key: "time",
    options: [
      { label: "Before the day starts", value: "morning" },
      { label: "After work", value: "evening" },
      { label: "Weekends", value: "community" },
      { label: "Whenever, honestly", value: "community" },
    ],
  },
  {
    q: "What is the goal this year?",
    key: "goal",
    options: [
      { label: "First 5K or return to running", value: "community" },
      { label: "A big PR", value: "competitive" },
      { label: "HYROX or hybrid racing", value: "training group" },
      { label: "Belonging somewhere", value: "community" },
    ],
  },
];

export function hrZones(age, restHr) {
  const max = 208 - 0.7 * age;
  const useHrr = restHr != null && restHr > 20 && restHr < max;
  const bands = [
    { zone: "Zone 1 · Recovery", pct: [0.5, 0.6], desc: "Very easy. Warm-ups, cool-downs, the day after a hard one." },
    { zone: "Zone 2 · Easy / Aerobic", pct: [0.6, 0.7], desc: "Conversational. Where most of your weekly miles should live." },
    { zone: "Zone 3 · Steady / Tempo", pct: [0.7, 0.8], desc: "Comfortably working. Marathon-to-steady effort." },
    { zone: "Zone 4 · Threshold", pct: [0.8, 0.9], desc: "Comfortably hard. Roughly one-hour race effort." },
    { zone: "Zone 5 · VO2 Max", pct: [0.9, 1.0], desc: "Hard intervals. Minutes at a time, full focus." },
  ];
  return {
    max: Math.round(max),
    method: useHrr ? "Karvonen (heart rate reserve)" : "Percent of max heart rate",
    zones: bands.map((b) => ({
      ...b,
      bpm: b.pct.map((p) => Math.round(useHrr ? restHr + (max - restHr) * p : max * p)),
    })),
  };
}

export function runCalories(weightKg, km, seconds) {
  const lb = weightKg * 2.20462;
  const mi = km / 1.60934;
  const gross = 0.75 * lb * mi;
  const net = 0.63 * lb * mi;
  return {
    gross: Math.round(gross),
    net: Math.round(net),
    perMile: Math.round(gross / mi),
    perHour: seconds ? Math.round(gross / (seconds / 3600)) : null,
  };
}

export function raceSplits(totalSeconds, km, unit, strategy) {
  const unitKm = unit === "mi" ? 1.60934 : 1;
  const nFull = Math.floor(km / unitKm + 1e-9);
  const segs = Array(nFull).fill(unitKm);
  const rem = km - nFull * unitKm;
  if (rem > 0.005) segs.push(rem);
  const avgPace = totalSeconds / km;
  const delta = strategy === "even" ? 0 : strategy === "negative" ? 0.02 : -0.02;
  const n = segs.length;
  let raw = segs.map((seg, i) => {
    const f = n === 1 ? 0.5 : i / (n - 1);
    return avgPace * (1 + delta - 2 * delta * f) * seg;
  });
  const scale = totalSeconds / raw.reduce((a, b) => a + b, 0);
  let cum = 0;
  return raw.map((t, i) => {
    const time = t * scale;
    cum += time;
    return {
      label: segs[i] < unitKm - 0.005 ? `Last ${unit === "mi" ? (segs[i] / 1.60934).toFixed(2) + " mi" : segs[i].toFixed(2) + " km"}` : `${unit === "mi" ? "Mile" : "KM"} ${i + 1}`,
      split: time,
      pace: time / (segs[i] / unitKm),
      cumulative: cum,
    };
  });
}

export function bmiOf(weightKg, heightCm) {
  const m = heightCm / 100;
  const bmi = weightKg / (m * m);
  const cat = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal range" : bmi < 30 ? "Overweight" : "Obese range";
  return { bmi: Math.round(bmi * 10) / 10, cat };
}

export function fuelingPlan(hours, weightKg) {
  const carbs = hours < 1.25 ? [0, 30] : hours <= 2.5 ? [30, 60] : [60, 90];
  return {
    carbsPerHour: carbs,
    totalCarbs: [Math.round(carbs[0] * hours), Math.round(carbs[1] * hours)],
    fluidPerHour: [400, 800],
    sodiumPerHour: [300, 600],
    carbLoad: hours >= 1.5 ? [Math.round(8 * weightKg), Math.round(10 * weightKg)] : null,
    gels: [Math.max(0, Math.round((carbs[0] * hours) / 25)), Math.max(1, Math.round((carbs[1] * hours) / 25))],
  };
}
